import { z } from 'zod';

export const ContactAddressSchema = z.object({
  street: z.string().nullish(),
  unit: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string().nullish(),
  lat: z.number().optional(),
  lng: z.number().optional(),
});
export type ContactAddress = z.infer<typeof ContactAddressSchema>;

const EMAIL_ADDRESS = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const EMAIL_MAX_LENGTH = 255;
/** `;` and `,` join multiple addresses into one value; such a value must never be stored. */
export const EMAIL_ADDRESS_DELIMITER = /[;,]/;

/** True only for a single address — joined lists such as `a@x.com;b@y.com` are rejected. */
export function isSingleEmailAddress(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  if (v.length > EMAIL_MAX_LENGTH) return false;
  if (EMAIL_ADDRESS_DELIMITER.test(v)) return false;
  return EMAIL_ADDRESS.test(v);
}

/** One non-empty, single email address (post-normalize). */
export const SingleEmailAddressSchema = z.string().refine(isSingleEmailAddress, {
  message: 'Email must be a single valid email address',
});

/**
 * Optional contact `email` — undefined/blank are allowed; otherwise must be a
 * single address (no `;` / `,` joined lists).
 */
export const ContactEmailSchema = z
  .string()
  .optional()
  .refine(
    (email) =>
      email === undefined ||
      email.trim() === '' ||
      isSingleEmailAddress(email.trim().toLowerCase()),
    { message: 'Email must be a single valid email address' }
  );

/**
 * Persisted `ccEmails` — Mongoose initializes this array even when empty.
 * Every element must be a single address.
 */
export const ContactCcEmailsSchema = z
  .array(z.string())
  .refine(
    (emails) =>
      emails.every((email) => isSingleEmailAddress(email.trim().toLowerCase())),
    { message: 'One or more email addresses are invalid or empty' }
  );

export const ContactSchema = z.object({
  _id: z.string(),
  _companyId: z.string(),
  _subCompanyId: z.string().optional(),
  _contactBusinessId: z.string().nullish(),
  type: z.literal('Contact'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  active: z.boolean(),
  email: ContactEmailSchema,
  ccEmails: ContactCcEmailsSchema,
  phone: z.string().optional(),
  title: z.string().optional(),
  address: ContactAddressSchema.nullish(),
  /** Mongoose virtual; present on normal JSON/object serialization, absent on inputs/lean results. */
  fullAddress: z.string().optional(),
  image: z.string().optional(),
  thumb: z.string().optional(),
  birthday: z.string().optional(),
  tags: z.array(z.string()).optional(),
  notes: z.string().optional(),
  privateNotes: z.string().optional(),
  leadSource: z.string().optional(),
  preferredTechIds: z.array(z.string()),
  pricingNotes: z.string().optional(),
  invoiceApproved: z.boolean(),
  baCount: z.number(),
  saCount: z.number(),
  preferredContactMethod: z.array(z.enum(['email', 'text'])),
  emailStatus: z.enum(['subscribed', 'unsubscribed']),
  smsStatus: z.enum(['subscribed', 'unsubscribed']),
  _spectoraId: z.string().optional(),
  _activeCampaignId: z.string().optional(),
  isnImportPartyKey: z.string().optional(),
  _mergedIntoContactId: z.string().nullish(),
  mergedAt: z.string().nullish(),
  mergedBy: z.string().nullish(),
  isDeleted: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Contact = z.infer<typeof ContactSchema>;
